import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject
} from '@angular/core';
import { forkJoin } from 'rxjs';
import { WidgetPanelCounts } from 'src/app/interfaces/widget-panel-counts';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { InsuranceService } from 'src/app/services/insurance.service';

@Component({
  selector: 'app-expiring-insurance-widget',
  templateUrl: './expiring-insurance-widget.component.html',
  styleUrls: ['./expiring-insurance-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ExpiringInsuranceWidgetComponent {
  private _cdr = inject(ChangeDetectorRef);
  expiringInsurances: WidgetPanelCounts['data'] = new Map([]);

  widgetPanelCounts: WidgetPanelCounts = {
    title: 'Expiring Insurances',
    hasData: false,
    data: this.expiringInsurances
  };

  constructor(
    private InsuranceService: InsuranceService,
    private CompanyVendorService: CompanyVendorService
  ) {
    forkJoin([
      this.InsuranceService.getInsurancePoliciesCount(
        Object.assign(this.countInsurancePolicies(30), {
          InsuranceCertificate: JSON.stringify(
            this.getInsuranceCertificateQuery()
          )
        })
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        this.countCompanyVendorsWithInsurancePolicies(30)
      ),
      this.InsuranceService.getInsurancePoliciesCount(
        Object.assign(this.countInsurancePolicies(60), {
          InsuranceCertificate: JSON.stringify(
            this.getInsuranceCertificateQuery()
          )
        })
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        this.countCompanyVendorsWithInsurancePolicies(60)
      ),
      this.InsuranceService.getInsurancePoliciesCount(
        Object.assign(this.countInsurancePolicies(90), {
          InsuranceCertificate: JSON.stringify(
            this.getInsuranceCertificateQuery()
          )
        })
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        this.countCompanyVendorsWithInsurancePolicies(90)
      ),

      this.CompanyVendorService.getCompanyVendorCount(
        this.countExpiringCAWC(30, 'WSIB')
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        this.countExpiredCAWC('WSIB')
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        this.countExpiringCAWC(30, 'WCB')
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        this.countExpiredCAWC('WCB')
      )
    ]).subscribe({
      next: ([
        insurancePolicesExpiring30DaysCount,
        vendorPoliciesExpiring30Days,
        insurancePolicesExpiring60DaysCount,
        vendorPoliciesExpiring60Days,
        insurancePolicesExpiring90DaysCount,
        vendorPoliciesExpiring90Days,

        expiringWSIB30DaysCount,
        expiredWSIB,
        expiringWCB30DaysCount,
        expiredWCB
      ]) => {
        this.expiringInsurances.set('Policies Expiring 30 days', {
          count: Number(insurancePolicesExpiring30DaysCount)
          // query: {
          //   options: JSON.stringify({ duplicating: false }),
          //   InsuranceCertificate: JSON.stringify(
          //     Object.assign(this.getInsuranceCertificateQuery(), {
          //       InsurancePolicy: this.countInsurancePolicies(30)
          //     })
          //   )
          // }
        });
        this.expiringInsurances.set('Vendor Policies Expiring 30 days', {
          count: Number(vendorPoliciesExpiring30Days),
          query: this.countCompanyVendorsWithInsurancePolicies(30)
        });
        this.expiringInsurances.set('Policies Expiring 60 days', {
          count: Number(insurancePolicesExpiring60DaysCount)
          // query: {
          //   InsuranceCertificate: JSON.stringify(
          //     Object.assign(this.getInsuranceCertificateQuery(), {
          //       InsurancePolicy: this.countInsurancePolicies(60)
          //     })
          //   )
          // }
        });
        this.expiringInsurances.set('Vendor Policies Expiring 60 days', {
          count: Number(vendorPoliciesExpiring60Days),
          query: this.countCompanyVendorsWithInsurancePolicies(60)
        });
        this.expiringInsurances.set('Policies Expiring 90 days', {
          count: Number(insurancePolicesExpiring90DaysCount)
          // query: {
          //   InsuranceCertificate: JSON.stringify(
          //     Object.assign(this.getInsuranceCertificateQuery(), {
          //       InsurancePolicy: this.countInsurancePolicies(90)
          //     })
          //   )
          // }
        });
        this.expiringInsurances.set('Vendor Policies Expiring 90 days', {
          count: Number(vendorPoliciesExpiring90Days),
          query: this.countCompanyVendorsWithInsurancePolicies(90)
        });

        this.expiringInsurances.set('WSIB Expiring in 30 Days', {
          count: Number(expiringWSIB30DaysCount),
          query: this.countExpiringCAWC(30, 'WSIB')
        });
        (this.expiringInsurances.set('WSIB Expired', {
          count: Number(expiredWSIB),
          query: this.countExpiredCAWC('WSIB')
        }),
          this.expiringInsurances.set('WCB Expiring in 30 Days', {
            count: Number(expiringWCB30DaysCount),
            query: this.countExpiringCAWC(30, 'WCB')
          }));
        this.expiringInsurances.set('WCB Expired', {
          count: Number(expiredWCB),
          query: this.countExpiredCAWC('WCB')
        });
        this._cdr.markForCheck();
      }
    });
  }

  private countCompanyVendorsWithInsurancePolicies(days: number) {
    const query = {
      expiringInsurancePolices: JSON.stringify({
        parameters: { days }
      })
    };

    return query;
  }

  private getInsuranceCertificateQuery() {
    const query = {
      options: JSON.stringify({ required: true }),
      status: 'Accepted',
      isPaused: false
    };

    return query;
  }

  private countInsurancePolicies(days: number) {
    const currentDate = new Date();
    const futureDate = new Date();

    futureDate.setDate(currentDate.getDate() + days);

    const query = {
      options: JSON.stringify({
        required: true
      }),
      expirationDate: JSON.stringify({
        $between: [currentDate, futureDate]
      }),
      status: JSON.stringify({
        $in: ['Active', 'Renewal', 'Expiring', 'Expired']
      })
    };

    return query;
  }

  private countExpiringCAWC(days: number, cawcType: 'WSIB' | 'WCB') {
    const currentDate = new Date();
    const futureDate = new Date();

    futureDate.setDate(currentDate.getDate() + days);

    const query = {
      country: 'CA',
      viveStatus: JSON.stringify({
        $notIn: [
          'Inactive',
          'Suspended',
          'Registering',
          'Awaiting Payment',
          'Compliance Exempt'
        ]
      }),
      [cawcType]: JSON.stringify({
        CAWCDate: {
          $and: [{ $ne: null }, { $between: [currentDate, futureDate] }]
        }
      })
    };

    return query;
  }

  private countExpiredCAWC(cawcType: 'WSIB' | 'WCB') {
    const query = {
      country: 'CA',
      viveStatus: JSON.stringify({
        $notIn: [
          'Inactive',
          'Suspended',
          'Registering',
          'Awaiting Payment',
          'Compliance Exempt'
        ]
      }),
      [cawcType]: JSON.stringify({
        options: { required: true },
        CAWCDate: {
          $and: [{ $ne: null }, { $lte: new Date() }]
        }
      })
    };

    return query;
  }
}
